import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import { permissionsFor, roleLabel } from '@/utils/permissions';

const uploadModule = {
  namespaced: true,
  state: {
    uploads: [], // Array de uploads ativos
    isUploading: false
  },
  mutations: {
    ADD_UPLOAD(state, upload) {
      state.uploads.push(upload);
      state.isUploading = state.uploads.some(u => u.status === 'uploading' || u.status === 'processing' || u.status === 'canceling' || u.status === 'queued');
    },
    UPDATE_UPLOAD_PROGRESS(state, { id, progress, status, error, uploadedSize, speed, serverFileId, cancelRequested }) {
      const upload = state.uploads.find(u => u.id === id);
      if (upload) {
        if (progress !== undefined) {
          // Garantir que o progresso esteja entre 0 e 100
          upload.progress = Math.max(0, Math.min(100, progress));
        }
        if (status !== undefined) upload.status = status;
        if (error !== undefined) upload.error = error;
        if (uploadedSize !== undefined) upload.uploadedSize = uploadedSize;
        if (speed !== undefined) upload.speed = speed;
        if (serverFileId !== undefined) upload.serverFileId = serverFileId;
        if (cancelRequested !== undefined) upload.cancelRequested = cancelRequested;
        
        state.isUploading = state.uploads.some(u => u.status === 'uploading' || u.status === 'processing' || u.status === 'canceling' || u.status === 'queued');
      }
    },
    REMOVE_UPLOAD(state, id) {
      const index = state.uploads.findIndex(u => u.id === id);
      if (index > -1) {
        state.uploads.splice(index, 1);
        state.isUploading = state.uploads.some(u => u.status === 'uploading' || u.status === 'processing' || u.status === 'canceling' || u.status === 'queued');
      }
    },
    CLEAR_COMPLETED_UPLOADS(state) {
      state.uploads = state.uploads.filter(u => u.status !== 'completed' && u.status !== 'failed');
      state.isUploading = state.uploads.some(u => u.status === 'uploading' || u.status === 'processing' || u.status === 'canceling' || u.status === 'queued');
    }
  },
  actions: {
    startUpload({ commit }, uploadData) {
      const upload = {
        id: Date.now() + Math.random(),
        file: uploadData.file,
        filename: uploadData.filename,
        progress: 0,
        status: 'uploading', // uploading, processing, canceling, completed, failed
        error: null,
        startTime: Date.now(),
        estimatedTime: null,
        speed: 0,
        size: uploadData.file.size,
        uploadedSize: 0,
        serverFileId: null,
        cancelRequested: false
      };
      commit('ADD_UPLOAD', upload);
      return upload.id;
    },
    // Cria uma entrada em espera (fila) antes de o upload realmente começar
    enqueueUpload({ commit }, uploadData) {
      const upload = {
        id: Date.now() + Math.random(),
        file: uploadData.file,
        filename: uploadData.filename,
        progress: 0,
        status: 'queued', // queued, uploading, processing, canceling, completed, failed
        error: null,
        startTime: null,
        estimatedTime: null,
        speed: 0,
        size: uploadData.file.size,
        uploadedSize: 0,
        serverFileId: null,
        cancelRequested: false
      };
      commit('ADD_UPLOAD', upload);
      return upload.id;
    },
    startQueuedUpload({ commit }, id) {
      commit('UPDATE_UPLOAD_PROGRESS', { id, status: 'uploading' });
    },
    updateProgress({ commit }, { id, progress, uploadedSize, speed }) {
      commit('UPDATE_UPLOAD_PROGRESS', { id, progress, uploadedSize, speed });
    },
    updateStatus({ commit }, { id, status, error }) {
      commit('UPDATE_UPLOAD_PROGRESS', { id, status, error });
    },
    setUploadServerFileId({ commit }, { id, serverFileId }) {
      commit('UPDATE_UPLOAD_PROGRESS', { id, serverFileId });
    },
    requestCancel({ commit }, id) {
      commit('UPDATE_UPLOAD_PROGRESS', { id, status: 'canceling', cancelRequested: true, speed: 0 });
    },
    clearCancelRequest({ commit }, id) {
      commit('UPDATE_UPLOAD_PROGRESS', { id, status: 'uploading', cancelRequested: false });
    },
    completeUpload({ commit }, id) {
      commit('UPDATE_UPLOAD_PROGRESS', { id, status: 'completed', progress: 100 });
      setTimeout(() => {
        commit('REMOVE_UPLOAD', id);
      }, 5000);
    },
    failUpload({ commit }, { id, error }) {
      commit('UPDATE_UPLOAD_PROGRESS', { id, status: 'failed', error });
      setTimeout(() => {
        commit('REMOVE_UPLOAD', id);
      }, 10000);
    },
    removeUpload({ commit }, id) {
      commit('REMOVE_UPLOAD', id);
    },
    clearCompleted({ commit }) {
      commit('CLEAR_COMPLETED_UPLOADS');
    }
  },
  getters: {
    activeUploads: state => state.uploads.filter(u => u.status === 'uploading' || u.status === 'processing' || u.status === 'canceling' || u.status === 'queued'),
    completedUploads: state => state.uploads.filter(u => u.status === 'completed'),
    failedUploads: state => state.uploads.filter(u => u.status === 'failed'),
    hasActiveUpload: state => state.uploads.some(u => u.status === 'uploading' || u.status === 'processing' || u.status === 'canceling' || u.status === 'queued'),
    totalProgress: state => {
      if (state.uploads.length === 0) return 0;
      const totalProgress = state.uploads.reduce((sum, upload) => sum + upload.progress, 0);
      const averageProgress = Math.round(totalProgress / state.uploads.length);
      // Garantir que o progresso total não passe de 100%
      return Math.min(averageProgress, 100);
    }
  }
};

const transcoderModule = {
  namespaced: true,
  state: {
    settings: null,
    drmIsActive: false,
    isLoading: false,
    error: null
  },
  mutations: {
    SET_TRANSCODER_SETTINGS(state, settings) {
      state.settings = settings;
      state.drmIsActive = settings.drmIsActive || false;
    },
    SET_DRM_ACTIVE(state, drmIsActive) {
      state.drmIsActive = drmIsActive;
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_TRANSCODER(state) {
      state.settings = null;
      state.drmIsActive = false;
      state.error = null;
    }
  },
  actions: {
    async fetchTranscoderSettings({ commit }, token) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        
        const { default: transcoder } = await import(
          /* webpackMode: "eager" */
          '../utils/transcoder.js'
        );
        const result = await transcoder.getTranscoderSettings(token);
        
        if (result && result.data) {
          commit('SET_TRANSCODER_SETTINGS', result.data);
        } else {
          throw new Error('Resposta inválida da API de transcoder');
        }
        
        commit('SET_LOADING', false);
        return result.data;
      } catch (error) {
        console.error('Erro ao buscar configurações do transcoder:', error);
        commit('SET_ERROR', error.message);
        commit('SET_LOADING', false);
        throw error;
      }
    },
    updateDrmStatus({ commit }, drmIsActive) {
      commit('SET_DRM_ACTIVE', drmIsActive);
    },
    clearTranscoder({ commit }) {
      commit('CLEAR_TRANSCODER');
    }
  },
  getters: {
    getTranscoderSettings: state => state.settings,
    isDrmActive: state => state.drmIsActive,
    isLoading: state => state.isLoading,
    hasError: state => !!state.error,
    getError: state => state.error
  }
};

const rootFolderModule = {
  namespaced: true,
  state: {
    rootFolder: null, // Dados da pasta raiz (primeiro item sem children)
    pagination: null, // Dados de paginação
    filters: null, // Filtros aplicados
    isLoading: false,
    error: null
  },
  mutations: {
    SET_ROOT_FOLDER(state, rootFolder) {
      state.rootFolder = {
        id: rootFolder.id,
        createdAt: rootFolder.createdAt,
        updatedAt: rootFolder.updatedAt,
        name: rootFolder.name,
        parentId: rootFolder.parentId,
        children: [], // Array vazio para manter a estrutura
        size: rootFolder.size,
        fileCount: rootFolder.fileCount,
        folderCount: rootFolder.folderCount,
        accountId: rootFolder.accountId,
        deletedAt: rootFolder.deletedAt
      };
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = pagination;
    },
    SET_FILTERS(state, filters) {
      state.filters = filters;
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ROOT_FOLDER(state) {
      state.rootFolder = null;
      state.pagination = null;
      state.filters = null;
      state.error = null;
    }
  },
  actions: {
    setRootFolderData({ commit }, data) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        
        if (data.folders && data.folders.length > 0) {
          const rootFolder = data.folders[0];
          commit('SET_ROOT_FOLDER', rootFolder);
        } else {
        }
        
        if (data.pagination) {
          commit('SET_PAGINATION', data.pagination);
        }
        
        if (data.filters) {
          commit('SET_FILTERS', data.filters);
        }
        
        commit('SET_LOADING', false);
      } catch (error) {
        console.error('Erro em setRootFolderData:', error);
        commit('SET_ERROR', error.message);
        commit('SET_LOADING', false);
      }
    },
    clearRootFolder({ commit }) {
      commit('CLEAR_ROOT_FOLDER');
    },
    updateRootFolderName({ commit, state }, newName) {
      if (state.rootFolder) {
        commit('SET_ROOT_FOLDER', {
          ...state.rootFolder,
          name: newName
        });
      }
    }
  },
  getters: {
    getRootFolder: state => state.rootFolder,
    getPagination: state => state.pagination,
    getFilters: state => state.filters,
    isLoading: state => state.isLoading,
    hasError: state => !!state.error,
    getError: state => state.error,
    hasRootFolder: state => !!state.rootFolder
  }
};

const filesCacheModule = {
  namespaced: true,
  state: {
    items: {}
  },
  mutations: {
    SET_FILES_CACHE(state, { key, data, expiresAt }) {
      state.items = {
        ...state.items,
        [key]: { data, expiresAt }
      };
    },
    CLEAR_FILES_CACHE(state) {
      state.items = {};
    },
    REMOVE_FILES_CACHE_KEY(state, key) {
      if (!state.items[key]) return;

      const updatedItems = { ...state.items };
      delete updatedItems[key];
      state.items = updatedItems;
    },
    CLEAN_EXPIRED_FILES_CACHE(state) {
      const now = Date.now();
      const updatedItems = {};

      Object.entries(state.items).forEach(([key, value]) => {
        if (value?.expiresAt && value.expiresAt > now) {
          updatedItems[key] = value;
        }
      });

      state.items = updatedItems;
    }
  },
  actions: {
    setFilesCache({ commit }, { key, data, ttlMs = 60000 }) {
      commit('SET_FILES_CACHE', {
        key,
        data,
        expiresAt: Date.now() + ttlMs
      });
    },
    clearFilesCache({ commit }) {
      commit('CLEAR_FILES_CACHE');
    },
    removeFilesCacheKey({ commit }, key) {
      commit('REMOVE_FILES_CACHE_KEY', key);
    },
    cleanupExpiredFilesCache({ commit }) {
      commit('CLEAN_EXPIRED_FILES_CACHE');
    }
  },
  getters: {
    getFilesCache: (state) => (key) => {
      const cacheEntry = state.items[key];
      if (!cacheEntry) return null;

      if (!cacheEntry.expiresAt || cacheEntry.expiresAt <= Date.now()) {
        return null;
      }

      return cacheEntry.data;
    }
  }
};

const foldersCacheModule = {
  namespaced: true,
  state: {
    items: {}
  },
  mutations: {
    SET_FOLDERS_CACHE(state, { key, data, expiresAt }) {
      state.items = {
        ...state.items,
        [key]: { data, expiresAt }
      };
    },
    CLEAR_FOLDERS_CACHE(state) {
      state.items = {};
    },
    REMOVE_FOLDERS_CACHE_KEY(state, key) {
      if (!state.items[key]) return;

      const updatedItems = { ...state.items };
      delete updatedItems[key];
      state.items = updatedItems;
    },
    CLEAN_EXPIRED_FOLDERS_CACHE(state) {
      const now = Date.now();
      const updatedItems = {};

      Object.entries(state.items).forEach(([key, value]) => {
        if (value?.expiresAt && value.expiresAt > now) {
          updatedItems[key] = value;
        }
      });

      state.items = updatedItems;
    }
  },
  actions: {
    setFoldersCache({ commit }, { key, data, ttlMs = 60000 }) {
      commit('SET_FOLDERS_CACHE', {
        key,
        data,
        expiresAt: Date.now() + ttlMs
      });
    },
    clearFoldersCache({ commit }) {
      commit('CLEAR_FOLDERS_CACHE');
    },
    removeFoldersCacheKey({ commit }, key) {
      commit('REMOVE_FOLDERS_CACHE_KEY', key);
    },
    cleanupExpiredFoldersCache({ commit }) {
      commit('CLEAN_EXPIRED_FOLDERS_CACHE');
    }
  },
  getters: {
    getFoldersCache: (state) => (key) => {
      const cacheEntry = state.items[key];
      if (!cacheEntry) return null;

      if (!cacheEntry.expiresAt || cacheEntry.expiresAt <= Date.now()) {
        return null;
      }

      return cacheEntry.data;
    }
  }
};

const reportsCacheModule = {
  namespaced: true,
  state: {
    items: {}
  },
  mutations: {
    SET_REPORTS_CACHE(state, { key, data, expiresAt }) {
      state.items = {
        ...state.items,
        [key]: { data, expiresAt }
      };
    },
    CLEAR_REPORTS_CACHE(state) {
      state.items = {};
    },
    REMOVE_REPORTS_CACHE_KEY(state, key) {
      if (!state.items[key]) return;

      const updatedItems = { ...state.items };
      delete updatedItems[key];
      state.items = updatedItems;
    },
    CLEAN_EXPIRED_REPORTS_CACHE(state) {
      const now = Date.now();
      const updatedItems = {};

      Object.entries(state.items).forEach(([key, value]) => {
        if (value?.expiresAt && value.expiresAt > now) {
          updatedItems[key] = value;
        }
      });

      state.items = updatedItems;
    }
  },
  actions: {
    setReportsCache({ commit }, { key, data, ttlMs = 60000 }) {
      commit('SET_REPORTS_CACHE', {
        key,
        data,
        expiresAt: Date.now() + ttlMs
      });
    },
    clearReportsCache({ commit }) {
      commit('CLEAR_REPORTS_CACHE');
    },
    removeReportsCacheKey({ commit }, key) {
      commit('REMOVE_REPORTS_CACHE_KEY', key);
    },
    cleanupExpiredReportsCache({ commit }) {
      commit('CLEAN_EXPIRED_REPORTS_CACHE');
    }
  },
  getters: {
    getReportsCache: (state) => (key) => {
      const cacheEntry = state.items[key];
      if (!cacheEntry) return null;

      if (!cacheEntry.expiresAt || cacheEntry.expiresAt <= Date.now()) {
        return null;
      }

      return cacheEntry.data;
    }
  }
};

const dashboardCacheModule = {
  namespaced: true,
  state: {
    items: {}
  },
  mutations: {
    SET_DASHBOARD_CACHE(state, { key, data, expiresAt }) {
      state.items = {
        ...state.items,
        [key]: { data, expiresAt }
      };
    },
    CLEAR_DASHBOARD_CACHE(state) {
      state.items = {};
    },
    REMOVE_DASHBOARD_CACHE_KEY(state, key) {
      if (!state.items[key]) return;

      const updatedItems = { ...state.items };
      delete updatedItems[key];
      state.items = updatedItems;
    },
    CLEAN_EXPIRED_DASHBOARD_CACHE(state) {
      const now = Date.now();
      const updatedItems = {};

      Object.entries(state.items).forEach(([key, value]) => {
        if (value?.expiresAt && value.expiresAt > now) {
          updatedItems[key] = value;
        }
      });

      state.items = updatedItems;
    }
  },
  actions: {
    setDashboardCache({ commit }, { key, data, ttlMs = 60000 }) {
      commit('SET_DASHBOARD_CACHE', {
        key,
        data,
        expiresAt: Date.now() + ttlMs
      });
    },
    clearDashboardCache({ commit }) {
      commit('CLEAR_DASHBOARD_CACHE');
    },
    removeDashboardCacheKey({ commit }, key) {
      commit('REMOVE_DASHBOARD_CACHE_KEY', key);
    },
    cleanupExpiredDashboardCache({ commit }) {
      commit('CLEAN_EXPIRED_DASHBOARD_CACHE');
    }
  },
  getters: {
    getDashboardCache: (state) => (key) => {
      const cacheEntry = state.items[key];
      if (!cacheEntry) return null;

      if (!cacheEntry.expiresAt || cacheEntry.expiresAt <= Date.now()) {
        return null;
      }

      return cacheEntry.data;
    }
  }
};

const historyCacheModule = {
  namespaced: true,
  state: {
    items: {}
  },
  mutations: {
    SET_HISTORY_CACHE(state, { key, data, expiresAt }) {
      state.items = {
        ...state.items,
        [key]: { data, expiresAt }
      };
    },
    CLEAR_HISTORY_CACHE(state) {
      state.items = {};
    },
    REMOVE_HISTORY_CACHE_KEY(state, key) {
      if (!state.items[key]) return;

      const updatedItems = { ...state.items };
      delete updatedItems[key];
      state.items = updatedItems;
    },
    CLEAN_EXPIRED_HISTORY_CACHE(state) {
      const now = Date.now();
      const updatedItems = {};

      Object.entries(state.items).forEach(([key, value]) => {
        if (value?.expiresAt && value.expiresAt > now) {
          updatedItems[key] = value;
        }
      });

      state.items = updatedItems;
    }
  },
  actions: {
    setHistoryCache({ commit }, { key, data, ttlMs = 60000 }) {
      commit('SET_HISTORY_CACHE', {
        key,
        data,
        expiresAt: Date.now() + ttlMs
      });
    },
    clearHistoryCache({ commit }) {
      commit('CLEAR_HISTORY_CACHE');
    },
    removeHistoryCacheKey({ commit }, key) {
      commit('REMOVE_HISTORY_CACHE_KEY', key);
    },
    cleanupExpiredHistoryCache({ commit }) {
      commit('CLEAN_EXPIRED_HISTORY_CACHE');
    }
  },
  getters: {
    getHistoryCache: (state) => (key) => {
      const cacheEntry = state.items[key];
      if (!cacheEntry) return null;

      if (!cacheEntry.expiresAt || cacheEntry.expiresAt <= Date.now()) {
        return null;
      }

      return cacheEntry.data;
    }
  }
};

const integrationsCacheModule = {
  namespaced: true,
  state: {
    items: {}
  },
  mutations: {
    SET_INTEGRATIONS_CACHE(state, { key, data, expiresAt }) {
      state.items = {
        ...state.items,
        [key]: { data, expiresAt }
      };
    },
    CLEAR_INTEGRATIONS_CACHE(state) {
      state.items = {};
    },
    CLEAN_EXPIRED_INTEGRATIONS_CACHE(state) {
      const now = Date.now();
      const updatedItems = {};

      Object.entries(state.items).forEach(([key, value]) => {
        if (value?.expiresAt && value.expiresAt > now) {
          updatedItems[key] = value;
        }
      });

      state.items = updatedItems;
    }
  },
  actions: {
    setIntegrationsCache({ commit }, { key, data, ttlMs = 60000 }) {
      commit('SET_INTEGRATIONS_CACHE', {
        key,
        data,
        expiresAt: Date.now() + ttlMs
      });
    },
    clearIntegrationsCache({ commit }) {
      commit('CLEAR_INTEGRATIONS_CACHE');
    },
    cleanupExpiredIntegrationsCache({ commit }) {
      commit('CLEAN_EXPIRED_INTEGRATIONS_CACHE');
    }
  },
  getters: {
    getIntegrationsCache: (state) => (key) => {
      const cacheEntry = state.items[key];
      if (!cacheEntry) return null;
      if (!cacheEntry.expiresAt || cacheEntry.expiresAt <= Date.now()) return null;
      return cacheEntry.data;
    }
  }
};

const configurationCacheModule = {
  namespaced: true,
  state: {
    items: {}
  },
  mutations: {
    SET_CONFIGURATION_CACHE(state, { key, data, expiresAt }) {
      state.items = {
        ...state.items,
        [key]: { data, expiresAt }
      };
    },
    CLEAR_CONFIGURATION_CACHE(state) {
      state.items = {};
    },
    CLEAN_EXPIRED_CONFIGURATION_CACHE(state) {
      const now = Date.now();
      const updatedItems = {};

      Object.entries(state.items).forEach(([key, value]) => {
        if (value?.expiresAt && value.expiresAt > now) {
          updatedItems[key] = value;
        }
      });

      state.items = updatedItems;
    }
  },
  actions: {
    setConfigurationCache({ commit }, { key, data, ttlMs = 60000 }) {
      commit('SET_CONFIGURATION_CACHE', {
        key,
        data,
        expiresAt: Date.now() + ttlMs
      });
    },
    clearConfigurationCache({ commit }) {
      commit('CLEAR_CONFIGURATION_CACHE');
    },
    cleanupExpiredConfigurationCache({ commit }) {
      commit('CLEAN_EXPIRED_CONFIGURATION_CACHE');
    }
  },
  getters: {
    getConfigurationCache: (state) => (key) => {
      const cacheEntry = state.items[key];
      if (!cacheEntry) return null;
      if (!cacheEntry.expiresAt || cacheEntry.expiresAt <= Date.now()) return null;
      return cacheEntry.data;
    }
  }
};

const usersCacheModule = {
  namespaced: true,
  state: {
    items: {}
  },
  mutations: {
    SET_USERS_CACHE(state, { key, data, expiresAt }) {
      state.items = {
        ...state.items,
        [key]: { data, expiresAt }
      };
    },
    CLEAR_USERS_CACHE(state) {
      state.items = {};
    },
    CLEAN_EXPIRED_USERS_CACHE(state) {
      const now = Date.now();
      const updatedItems = {};

      Object.entries(state.items).forEach(([key, value]) => {
        if (value?.expiresAt && value.expiresAt > now) {
          updatedItems[key] = value;
        }
      });

      state.items = updatedItems;
    }
  },
  actions: {
    setUsersCache({ commit }, { key, data, ttlMs = 60000 }) {
      commit('SET_USERS_CACHE', {
        key,
        data,
        expiresAt: Date.now() + ttlMs
      });
    },
    clearUsersCache({ commit }) {
      commit('CLEAR_USERS_CACHE');
    },
    cleanupExpiredUsersCache({ commit }) {
      commit('CLEAN_EXPIRED_USERS_CACHE');
    }
  },
  getters: {
    getUsersCache: (state) => (key) => {
      const cacheEntry = state.items[key];
      if (!cacheEntry) return null;
      if (!cacheEntry.expiresAt || cacheEntry.expiresAt <= Date.now()) return null;
      return cacheEntry.data;
    }
  }
};

export default createStore({
  plugins: [createPersistedState({
    paths: ['token', 'refreshToken', 'user', 'darkMode', 'currentFile', 'breadcrumbs', 'embedSettings', 'account', 'activeSidebar', 'foldersList', 'currentFolder', 'allChildrenFolders', 'rootFolder', 'transcoder', 'filesCache', 'foldersCache', 'reportsCache', 'dashboardCache', 'historyCache', 'integrationsCache', 'configurationCache', 'usersCache'],
    storage: window.localStorage
  })],
  modules: {
    upload: uploadModule,
    rootFolder: rootFolderModule,
    transcoder: transcoderModule,
    filesCache: filesCacheModule,
    foldersCache: foldersCacheModule,
    reportsCache: reportsCacheModule,
    dashboardCache: dashboardCacheModule,
    historyCache: historyCacheModule,
    integrationsCache: integrationsCacheModule,
    configurationCache: configurationCacheModule,
    usersCache: usersCacheModule
  },
  state: {
    openSidebarPrimary: false,
    hoverSidebarPrimary: false,
    fileCount: 0,
    folderCount: 0,
    token: null,
    refreshToken: null,
    user: [],
    currentIndexSidebar: 0,
    darkMode: (() => {
      // Carrega darkMode do localStorage na inicialização
      try {
        const saved = JSON.parse(localStorage.getItem('vuex'));
        return saved?.darkMode || false;
      } catch (e) {
        return false;
      }
    })(),
    currentFile: null,
    breadcrumbs: [],
    embedSettings: null,
    account: null,
    activeSidebar: false,
    foldersList: [],
    currentFolder: null,
    allChildrenFolders: [] // Array para armazenar todas as pastas filhas
  },
  getters: {
    // Nome do perfil do usuário logado (tolerante a diferentes formatos)
    currentRole(state) {
      const u = state.user || {};
      return u.roleName || u.role?.name || u.role || '';
    },
    // Rótulo em português do perfil atual (ex: "Administrador")
    currentRoleLabel(state, getters) {
      return roleLabel(getters.currentRole);
    },
    // Objeto com todas as permissões do perfil atual
    permissions(state, getters) {
      return permissionsFor(getters.currentRole);
    },
    // Atalhos booleanos para usar com v-if nos componentes
    canCreate(state, getters) { return getters.permissions.create; },
    canEdit(state, getters) { return getters.permissions.edit; },
    canDelete(state, getters) { return getters.permissions.delete; },
    canAdmin(state, getters) { return getters.permissions.admin; },
  },
  mutations: {
    setFileCount(state, count) {
      state.fileCount = count;
    },
    toggleSidebarPrimary(state) {
      state.openSidebarPrimary = !state.openSidebarPrimary;
    },
    openSidebarPrimary(state) {
      state.openSidebarPrimary = true;
    },
    closeSidebarPrimary(state) {
      state.openSidebarPrimary = false;
    },
    setHoverSidebarPrimary(state, isHover) {
      state.hoverSidebarPrimary = isHover;
    },
    setFolderCount(state, count) {
      state.folderCount = count;
    },
    setToken(state, token) {
      state.token = token;
    },
    setRefreshToken(state, refreshToken) {
      state.refreshToken = refreshToken;
    },
    setUser(state, user) {
      state.user = user;
    },
    setCurrentIndexSidebar(state, currentIndexSidebar) {
      state.currentIndexSidebar = currentIndexSidebar;
    },
    setDarkMode(state, darkMode) {
      state.darkMode = darkMode;
    },
    setCurrentFile(state, currentFile) {
      state.currentFile = currentFile;
    },
    setBreadcrumbs(state, breadcrumbs) {
      state.breadcrumbs = breadcrumbs;
    },
    setEmbedSettings(state, embedSettings) {
      state.embedSettings = embedSettings;
    },
    setAccount(state, account) {
      state.account = account;
    },
    setActiveSidebar(state, activeSidebar) {
      state.activeSidebar = activeSidebar;
    },
    setFoldersList(state, foldersList) {
      state.foldersList = foldersList;
    },
    setCurrentFolder(state, currentFolder) {
      state.currentFolder = currentFolder;
    },
    setAllChildrenFolders(state, folders) {
      state.allChildrenFolders = folders;
    }
  },
  actions: {
    storeFileCount({ commit }, payload) {
      commit('setFileCount', payload.count);
    },
    storeActiveSidebar({ commit }, payload) {
      commit('setActiveSidebar', payload.activeSidebar);
    },
    storeFolderCount({ commit }, payload) {
      commit('setFolderCount', payload.count);
    },
    storeToken({ commit }, payload) {
      commit('setToken', payload.token);
    },
    storeRefreshToken({ commit }, payload) {
      commit('setRefreshToken', payload.refreshToken);
    },
    storeUser({ commit }, payload) {
      commit('setUser', payload.user);
    },
    storeToggleSidebar({ commit }) {
      commit('toggleSidebarPrimary');
    },
    storeOpenSidebar({ commit }) {
      commit('openSidebarPrimary');
    },
    storeCloseSidebar({ commit }) {
      commit('closeSidebarPrimary');
    },
    storeHoverSidebar({ commit }, payload) {
      commit('setHoverSidebarPrimary', payload.isHover);
    },
    storeCurrentIndexSidebar({ commit }, payload) {
      commit('setCurrentIndexSidebar', payload.currentIndexSidebar);
    },
    storeDarkMode({ commit }, payload) {
      commit('setDarkMode', payload.darkMode);
    },
    storeCurrentFile({ commit }, payload) {
      commit('setCurrentFile', payload.currentFile);
    },
    storeBreadcrumbs({ commit }, payload) {
      commit('setBreadcrumbs', payload.breadcrumbs);
    },
    storeEmbedSettings({ commit }, payload) {
      commit('setEmbedSettings', payload.embedSettings);
    },
    storeAccount({ commit }, payload) {
      commit('setAccount', payload.account);
    },
    storeFoldersList({ commit }, payload) {
      commit('setFoldersList', payload.foldersList);
    },
    storeCurrentFolder({ commit }, payload) {
      commit('setCurrentFolder', payload.currentFolder);
    },
    storeAllChildrenFolders({ commit }, payload) {
      commit('setAllChildrenFolders', payload.folders);
    },
    clearSelectedFolder({ commit }) {
      commit('setCurrentFolder', null);
    },
    clearAllChildrenFolders({ commit }) {
      commit('setAllChildrenFolders', []);
    },
    clearAuth({ commit, dispatch }) {
      // Preservar darkMode do localStorage antes de limpar
      const savedDarkMode = (() => {
        try {
          const saved = JSON.parse(localStorage.getItem('vuex'));
          return saved?.darkMode;
        } catch (e) {
          return undefined;
        }
      })();
      
      commit('setToken', null);
      commit('setRefreshToken', null);
      commit('setUser', null);
      commit('setCurrentIndexSidebar', 0);
      // Não limpar darkMode - preservar preferência do usuário
      commit('setCurrentFile', null);
      commit('setBreadcrumbs', []);
      commit('setFileCount', 0);
      commit('setFolderCount', 0);
      commit('setActiveSidebar', false);
      commit('setEmbedSettings', null);
      commit('setAccount', null);
      commit('setFoldersList', []);
      commit('setCurrentFolder', null);
      commit('setAllChildrenFolders', []);
      // Limpar dados do transcoder
      commit('transcoder/CLEAR_TRANSCODER');
      // Limpar todos os caches de dados da sessão
      dispatch('clearAllCaches');

      // Restaurar darkMode após limpeza para garantir persistência
      // Usa setTimeout para garantir que seja executado após o vuex-persistedstate processar
      setTimeout(() => {
        if (savedDarkMode !== undefined) {
          commit('setDarkMode', savedDarkMode);
        }
      }, 0);
    },
    clearTokenForTest({ commit }) {
      commit('setToken', null);
    },
    // Limpa todos os caches dos módulos (usado ao trocar de sessão/cliente)
    clearAllCaches({ dispatch }) {
      dispatch('filesCache/clearFilesCache');
      dispatch('foldersCache/clearFoldersCache');
      dispatch('reportsCache/clearReportsCache');
      dispatch('dashboardCache/clearDashboardCache');
      dispatch('historyCache/clearHistoryCache');
      dispatch('integrationsCache/clearIntegrationsCache');
      dispatch('configurationCache/clearConfigurationCache');
      dispatch('usersCache/clearUsersCache');
    }
  },
});
